import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { CHECKLIST_ITEMS, checklistItemsForSchemaVersion } from '@/db/checklist'
import { validateInspectionForCompletion } from '@/db/completionValidation'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { summarizeInspectionResults } from '@/db/rules'
import { inspectionSearchFields } from '@/db/normalization'
import {
  createInspectionInputSchema,
  inspectionResultSchema,
  inspectionSchema,
  updateInspectionInputSchema,
} from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import { INSPECTION_SCHEMA_VERSION } from '@/db/version'
import type {
  Client,
  ClientSnapshot,
  CreateInspectionInput,
  Inspection,
  InspectionResult,
  Machine,
  MachineSnapshot,
  Technician,
  TechnicianSnapshot,
  UpdateInspectionInput,
} from '@/db/types'
import { changedFields, nextCode, requireRecord, writeAuditLog } from './helpers'

function clientSnapshot(client: Client): ClientSnapshot {
  const snapshot: ClientSnapshot = {
    code: client.code,
    companyName: client.companyName,
    farmOrSite: '',
    contactName: client.contactName,
    phone: client.phone,
  }
  if (client.email) snapshot.email = client.email
  if (client.document) snapshot.document = client.document
  if (client.address) snapshot.address = client.address
  return snapshot
}

function machineSnapshot(machine: Machine): MachineSnapshot {
  const snapshot: MachineSnapshot = {
    code: machine.code,
    equipment: machine.equipment,
    brand: machine.brand,
    model: machine.model,
    serialChassis: machine.serialChassis,
  }
  if (machine.year) snapshot.year = machine.year
  return snapshot
}

function technicianSnapshot(technician: Technician): TechnicianSnapshot {
  return { name: technician.name, role: technician.role, phone: technician.phone }
}

function currentCivilDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export class InspectionRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async createDraft(input: CreateInspectionInput): Promise<Inspection> {
    return executeDatabaseOperation('criar inspeção', async () => {
      const data = createInspectionInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.technicians,
          this.database.inspections,
          this.database.inspectionResults,
          this.database.counters,
          this.database.auditLogs,
        ],
        async () => {
          const [client, machine, technician] = await Promise.all([
            this.database.clients.get(data.clientId),
            this.database.machines.get(data.machineId),
            this.database.technicians.get(data.technicianId),
          ])
          const requiredClient = requireRecord(client, 'Cliente')
          const requiredMachine = requireRecord(machine, 'Máquina')
          const requiredTechnician = requireRecord(technician, 'Técnico')
          if (!requiredClient.active || !requiredMachine.active || !requiredTechnician.active) {
            throw new DatabaseOperationError(
              'relationship',
              'A inspeção exige cliente, máquina e técnico ativos.',
            )
          }
          if (requiredMachine.clientId !== requiredClient.id) {
            throw new DatabaseOperationError(
              'relationship',
              'A máquina selecionada não pertence ao cliente.',
            )
          }

          const now = new Date().toISOString()
          const inspectionId = createUuid()
          const inspection = inspectionSchema.parse({
            id: inspectionId,
            reportNumber: await nextCode(this.database, 'reportNumber', now),
            clientId: requiredClient.id,
            machineId: requiredMachine.id,
            technicianId: requiredTechnician.id,
            clientSnapshot: clientSnapshot(requiredClient),
            machineSnapshot: machineSnapshot(requiredMachine),
            technicianSnapshot: technicianSnapshot(requiredTechnician),
            ...inspectionSearchFields({
              clientSnapshot: clientSnapshot(requiredClient),
              machineSnapshot: machineSnapshot(requiredMachine),
              technicianSnapshot: technicianSnapshot(requiredTechnician),
            }),
            inspectionDate: data.inspectionDate,
            hourmeter: data.hourmeter,
            status: 'draft',
            currentStep: 1,
            diagnosis: data.diagnosis ?? '',
            priority: 'pending',
            authorization: 'unset',
            responsibleName: '',
            clientSignatureWaiverReason: '',
            revisionNumber: 0,
            createdAt: now,
            updatedAt: now,
            schemaVersion: INSPECTION_SCHEMA_VERSION,
          })
          const results = CHECKLIST_ITEMS.map((item): InspectionResult =>
            inspectionResultSchema.parse({
              id: createUuid(),
              inspectionId,
              checklistItemId: item.id,
              checklistItemLabel: item.label,
              itemOrder: item.order,
              systemId: item.systemId,
              systemLabel: item.systemLabel,
              systemOrder: item.systemOrder,
              status: 'unanswered',
              observation: '',
              createdAt: now,
              updatedAt: now,
            }),
          )

          await this.database.inspections.add(inspection)
          await this.database.inspectionResults.bulkAdd(results)
          await writeAuditLog(
            this.database,
            'inspection',
            inspection.id,
            'create',
            { reportNumber: inspection.reportNumber, resultCount: results.length },
            now,
          )
          return inspection
        },
      )
    })
  }

  async duplicateAsDraft(sourceInspectionId: string): Promise<Inspection> {
    return executeDatabaseOperation('duplicar inspeção', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.technicians,
          this.database.inspections,
          this.database.inspectionResults,
          this.database.counters,
          this.database.auditLogs,
        ],
        async () => {
          const source = inspectionSchema.parse(
            requireRecord(
              await this.database.inspections.get(sourceInspectionId),
              'Inspeção de origem',
            ),
          )
          const [client, machine, technician, sourceResults] = await Promise.all([
            this.database.clients.get(source.clientId),
            this.database.machines.get(source.machineId),
            this.database.technicians.get(source.technicianId),
            this.database.inspectionResults
              .where('inspectionId')
              .equals(sourceInspectionId)
              .toArray(),
          ])
          const requiredClient = requireRecord(client, 'Cliente')
          const requiredMachine = requireRecord(machine, 'Máquina')
          const requiredTechnician = requireRecord(technician, 'Técnico')
          if (!requiredClient.active || !requiredMachine.active || !requiredTechnician.active) {
            throw new DatabaseOperationError(
              'relationship',
              'Ative o cliente, a máquina e o técnico antes de duplicar este relatório.',
            )
          }
          if (requiredMachine.clientId !== requiredClient.id) {
            throw new DatabaseOperationError(
              'relationship',
              'A máquina da inspeção não pertence mais ao cliente informado.',
            )
          }
          const parsedSourceResults = sourceResults.map((result) =>
            inspectionResultSchema.parse(result),
          )
          const sourceChecklist = checklistItemsForSchemaVersion(source.schemaVersion)
          if (parsedSourceResults.length !== sourceChecklist.length) {
            throw new DatabaseOperationError(
              'invalid_state',
              'A inspeção de origem não possui o checklist completo para duplicação.',
            )
          }
          const sourceByItem = new Map(
            parsedSourceResults.map((result) => [result.checklistItemId, result]),
          )
          const now = new Date().toISOString()
          const id = createUuid()
          const snapshots = {
            clientSnapshot: clientSnapshot(requiredClient),
            machineSnapshot: machineSnapshot(requiredMachine),
            technicianSnapshot: technicianSnapshot(requiredTechnician),
          }
          const results = CHECKLIST_ITEMS.map((item): InspectionResult => {
            const previous =
              source.schemaVersion < 5 && item.id === '4.7'
                ? sourceByItem.get('4.8')
                : sourceByItem.get(item.id)
            const addedAfterSource =
              source.schemaVersion < INSPECTION_SCHEMA_VERSION &&
              (item.id === '6.5' || item.id === '6.6')
            if (!previous) {
              if (addedAfterSource) {
                return inspectionResultSchema.parse({
                  id: createUuid(),
                  inspectionId: id,
                  checklistItemId: item.id,
                  checklistItemLabel: item.label,
                  itemOrder: item.order,
                  systemId: item.systemId,
                  systemLabel: item.systemLabel,
                  systemOrder: item.systemOrder,
                  status: 'unanswered',
                  observation: '',
                  createdAt: now,
                  updatedAt: now,
                })
              }
              throw new DatabaseOperationError(
                'invalid_state',
                `O item ${item.id} não existe na inspeção de origem.`,
              )
            }
            return inspectionResultSchema.parse({
              id: createUuid(),
              inspectionId: id,
              checklistItemId: item.id,
              checklistItemLabel: item.label,
              itemOrder: item.order,
              systemId: item.systemId,
              systemLabel: item.systemLabel,
              systemOrder: item.systemOrder,
              status: previous.status,
              observation: previous.observation,
              createdAt: now,
              updatedAt: now,
            })
          })
          const inspection = inspectionSchema.parse({
            id,
            reportNumber: await nextCode(this.database, 'reportNumber', now),
            clientId: requiredClient.id,
            machineId: requiredMachine.id,
            technicianId: requiredTechnician.id,
            ...snapshots,
            ...inspectionSearchFields(snapshots),
            inspectionDate: currentCivilDate(),
            hourmeter: Math.max(source.hourmeter, requiredMachine.currentHourmeter),
            status: 'draft',
            currentStep: 1,
            diagnosis: source.diagnosis,
            priority: summarizeInspectionResults(results).priority,
            authorization: 'unset',
            responsibleName: '',
            clientSignatureWaiverReason: '',
            revisionNumber: 0,
            sourceInspectionId,
            createdAt: now,
            updatedAt: now,
            schemaVersion: INSPECTION_SCHEMA_VERSION,
          })
          await this.database.inspections.add(inspection)
          await this.database.inspectionResults.bulkAdd(results)
          await writeAuditLog(
            this.database,
            'inspection',
            inspection.id,
            'create',
            {
              reportNumber: inspection.reportNumber,
              sourceInspectionId,
              duplicatedResultCount: results.length,
            },
            now,
          )
          return inspection
        },
      ),
    )
  }

  async getById(id: string): Promise<Inspection | undefined> {
    return executeDatabaseOperation('consultar inspeção', async () => {
      const inspection = await this.database.inspections.get(id)
      return inspection ? inspectionSchema.parse(inspection) : undefined
    })
  }

  async getByReportNumber(reportNumber: string): Promise<Inspection | undefined> {
    return executeDatabaseOperation('consultar relatório', async () => {
      const inspection = await this.database.inspections
        .where('reportNumber')
        .equals(reportNumber)
        .first()
      return inspection ? inspectionSchema.parse(inspection) : undefined
    })
  }

  async update(id: string, input: UpdateInspectionInput): Promise<Inspection> {
    return executeDatabaseOperation('editar inspeção', async () => {
      const data = updateInspectionInputSchema.parse(input)
      return this.database.transaction(
        'rw',
        [this.database.inspections, this.database.signatures, this.database.auditLogs],
        async () => {
          const existing = inspectionSchema.parse(
            requireRecord(await this.database.inspections.get(id), 'Inspeção'),
          )
          if (existing.status !== 'draft') {
            throw new DatabaseOperationError(
              'invalid_state',
              'Somente inspeção em rascunho pode ser editada.',
            )
          }
          if (data.clientSignatureId) {
            const signature = requireRecord(
              await this.database.signatures.get(data.clientSignatureId),
              'Assinatura do cliente',
            )
            if (signature.inspectionId !== id || signature.type !== 'client') {
              throw new DatabaseOperationError('relationship', 'Assinatura do cliente inválida.')
            }
          }
          if (data.technicianSignatureId) {
            const signature = requireRecord(
              await this.database.signatures.get(data.technicianSignatureId),
              'Assinatura do técnico',
            )
            if (signature.inspectionId !== id || signature.type !== 'technician') {
              throw new DatabaseOperationError('relationship', 'Assinatura do técnico inválida.')
            }
          }
          const now = new Date().toISOString()
          const nextRecord: Inspection = { ...existing, ...data, updatedAt: now }
          if (data.authorization !== undefined) {
            if (data.authorization === 'unset') {
              delete nextRecord.authorizationAt
            } else if (data.authorization !== existing.authorization || !existing.authorizationAt) {
              nextRecord.authorizationAt = now
            }
            if (data.authorization !== 'no') {
              nextRecord.clientSignatureWaiverReason = ''
              delete nextRecord.clientSignatureWaiverConfirmedAt
            }
          }
          if (
            data.clientSignatureWaiverReason !== undefined &&
            data.clientSignatureWaiverReason.trim() !== existing.clientSignatureWaiverReason &&
            data.clientSignatureWaiverConfirmedAt === undefined
          ) {
            delete nextRecord.clientSignatureWaiverConfirmedAt
          }
          const inspection = inspectionSchema.parse(nextRecord)
          await this.database.inspections.put(inspection)
          await writeAuditLog(this.database, 'inspection', id, 'update', changedFields(data), now)
          return inspection
        },
      )
    })
  }

  async complete(id: string): Promise<Inspection> {
    return executeDatabaseOperation('concluir inspeção', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.clients,
          this.database.machines,
          this.database.technicians,
          this.database.inspections,
          this.database.inspectionResults,
          this.database.photos,
          this.database.photoPreviews,
          this.database.signatures,
          this.database.auditLogs,
        ],
        async () => {
          const inspection = inspectionSchema.parse(
            requireRecord(await this.database.inspections.get(id), 'Inspeção'),
          )
          if (inspection.status !== 'draft') {
            throw new DatabaseOperationError('invalid_state', 'A inspeção não está em rascunho.')
          }
          const [client, machine, technician, results, photos, previews] = await Promise.all([
            this.database.clients.get(inspection.clientId),
            this.database.machines.get(inspection.machineId),
            this.database.technicians.get(inspection.technicianId),
            this.database.inspectionResults.where('inspectionId').equals(id).toArray(),
            this.database.photos.where('inspectionId').equals(id).toArray(),
            this.database.photoPreviews.where('inspectionId').equals(id).toArray(),
          ])
          const parsedResults = results.map((result) => inspectionResultSchema.parse(result))
          const summary = summarizeInspectionResults(parsedResults)
          const [clientSignature, technicianSignature] = await Promise.all([
            inspection.clientSignatureId
              ? this.database.signatures.get(inspection.clientSignatureId)
              : undefined,
            inspection.technicianSignatureId
              ? this.database.signatures.get(inspection.technicianSignatureId)
              : undefined,
          ])
          const issues = validateInspectionForCompletion({
            inspection,
            results: parsedResults,
            ...(clientSignature ? { clientSignature } : {}),
            ...(technicianSignature ? { technicianSignature } : {}),
            photos,
            previews,
            relationships: {
              clientExists: Boolean(client),
              machineExists: Boolean(machine),
              technicianExists: Boolean(technician),
              machineBelongsToClient: machine?.clientId === inspection.clientId,
            },
          })
          if (issues.length > 0) {
            throw new DatabaseOperationError(
              'invalid_state',
              `Conclusão bloqueada: ${issues[0]?.message ?? 'existem dados pendentes'}`,
              issues[0]?.message ?? 'Revise os dados obrigatórios antes de concluir.',
            )
          }

          const now = new Date().toISOString()
          const completed = inspectionSchema.parse({
            ...inspection,
            status: 'completed',
            priority: summary.priority,
            completedAt: now,
            updatedAt: now,
          })
          await this.database.inspections.put(completed)
          if (machine && inspection.hourmeter > machine.currentHourmeter) {
            await this.database.machines.update(machine.id, {
              currentHourmeter: inspection.hourmeter,
              updatedAt: now,
            })
          }
          await writeAuditLog(
            this.database,
            'inspection',
            id,
            'complete',
            {
              revisionNumber: completed.revisionNumber,
              totals: {
                good: summary.good,
                attention: summary.attention,
                critical: summary.critical,
              },
              priority: summary.priority,
            },
            now,
          )
          return completed
        },
      ),
    )
  }

  async confirmSignatureWaiver(id: string, reason: string): Promise<Inspection> {
    const normalizedReason = reason.trim()
    if (normalizedReason.length < 3) {
      throw new DatabaseOperationError(
        'validation',
        'Explique em ao menos 3 caracteres por que o cliente não assinou.',
      )
    }
    return this.update(id, {
      clientSignatureWaiverReason: normalizedReason,
      clientSignatureWaiverConfirmedAt: new Date().toISOString(),
    })
  }

  async reopen(id: string, reason: string): Promise<Inspection> {
    const normalizedReason = reason.trim()
    if (normalizedReason.length < 3) {
      throw new DatabaseOperationError(
        'validation',
        'Informe o motivo da reabertura com ao menos 3 caracteres.',
      )
    }
    return executeDatabaseOperation('reabrir inspeção', async () =>
      this.database.transaction(
        'rw',
        [this.database.inspections, this.database.auditLogs],
        async () => {
          const existing = inspectionSchema.parse(
            requireRecord(await this.database.inspections.get(id), 'Inspeção'),
          )
          if (existing.status !== 'completed' || !existing.completedAt) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Somente um relatório concluído pode ser reaberto para revisão.',
            )
          }
          const now = new Date().toISOString()
          const reopened = inspectionSchema.parse({
            ...existing,
            status: 'draft',
            revisionNumber: existing.revisionNumber + 1,
            reopenedAt: now,
            reopenReason: normalizedReason,
            updatedAt: now,
          })
          await this.database.inspections.put(reopened)
          await writeAuditLog(
            this.database,
            'inspection',
            id,
            'reopen',
            {
              reason: normalizedReason,
              previousCompletedAt: existing.completedAt,
              revisionNumber: reopened.revisionNumber,
            },
            now,
          )
          return reopened
        },
      ),
    )
  }

  async cancel(id: string, reason: string): Promise<Inspection> {
    return executeDatabaseOperation('cancelar inspeção', async () =>
      this.database.transaction(
        'rw',
        [this.database.inspections, this.database.auditLogs],
        async () => {
          const existing = inspectionSchema.parse(
            requireRecord(await this.database.inspections.get(id), 'Inspeção'),
          )
          if (existing.status !== 'draft' || !reason.trim()) {
            throw new DatabaseOperationError(
              'invalid_state',
              'Somente rascunho pode ser cancelado e o motivo é obrigatório.',
            )
          }
          const now = new Date().toISOString()
          const inspection = inspectionSchema.parse({
            ...existing,
            status: 'cancelled',
            cancellationReason: reason.trim(),
            cancelledAt: now,
            updatedAt: now,
          })
          await this.database.inspections.put(inspection)
          await writeAuditLog(this.database, 'inspection', id, 'cancel', {}, now)
          return inspection
        },
      ),
    )
  }
}
